/**
 * View model for the Demand Explorer screen (task 14.3, R7.2, UI spec §5.2).
 *
 * Regroups the flat driver list the demand endpoint returns along whichever
 * dimension the reader picks, deriving each group's Demand_Hours, Initiative
 * count, Data_Incomplete count and share of the total, plus the per-period
 * breakdown the workload heatmap renders.
 *
 * Master data names are joined in here rather than being fetched per row: the
 * wire carries Activity_Type and Region ids (see `demand-explorer-data.ts`), and
 * the screen already holds those sets.
 */
import type { DemandDriver } from './demand-explorer-data';
import { ffRoleLabel } from '../ff-role-options';
import type { FieldForceRole } from '@complan/domain';

/** Grouping dimensions for the Demand Explorer table (UI spec §5.2). */
export const GROUP_BY_OPTIONS = ['initiative', 'ff-role', 'activity-type', 'region'] as const;

/** See {@link GROUP_BY_OPTIONS}. */
export type GroupBy = (typeof GROUP_BY_OPTIONS)[number];

/** A master data id → display name lookup, as the screen assembles it. */
export type NameLookup = ReadonlyMap<string, string>;

/** The lookups the grouping needs to render names instead of ids. */
export interface DemandExplorerLookups {
  readonly activityTypes: NameLookup;
  readonly regions: NameLookup;
}

/** One period's contribution to a group's annual total (UI spec §5.2). */
export interface PeriodBreakdown {
  readonly periodId: string;
  readonly periodLabel: string;
  readonly demandHours: number;
  /** This period's share of the group's own total, 0-100. */
  readonly sharePercent: number;
}

/** One row of the re-groupable Demand Explorer table (UI spec §5.2). */
export interface DemandExplorerRow {
  readonly key: string;
  readonly groupLabel: string;
  /**
   * The Initiative this row *is*, when grouping by Initiative — the row's own
   * Detail_Panel target — and `null` under every other grouping.
   *
   * Stated as its own field rather than left for the table to read out of `key`:
   * under `ff-role`, `activity-type` or `region` a row aggregates many Initiatives,
   * so there is no single record to open, and the difference between "this key is an
   * Initiative id" and "this key is a role" is the grouping's business, not the
   * renderer's.
   */
  readonly initiativeId: string | null;
  readonly initiativeCount: number;
  readonly driverCount: number;
  readonly demandHours: number;
  readonly dataIncompleteCount: number;
  /** This group's share of the overall total, 0-100. */
  readonly sharePercent: number;
  readonly periodBreakdown: readonly PeriodBreakdown[];
}

/**
 * The table's footing: the figures the grouped rows must add up to (R5.7).
 *
 * Not a sum of the rows. `initiativeCount` is counted across the drivers, because
 * grouping by role or activity type puts the same Initiative in several rows and
 * adding the per-row counts would report it twice. `demandHours` is the endpoint's
 * own total, so the footing is the independent figure the rows reconcile *against*
 * rather than a restatement of them.
 */
export interface DemandExplorerTotals {
  readonly initiativeCount: number;
  readonly driverCount: number;
  readonly demandHours: number;
  readonly dataIncompleteCount: number;
}

/** One Platform's row in the annual workload heatmap (R5.3, UI spec §5.2). */
export interface PlatformHeatmapRow {
  readonly platformId: string;
  readonly label: string;
  /** The Platform's configured colour, or `null` when none is set (R16.1). */
  readonly colour: string | null;
  readonly cells: readonly PlatformHeatmapCell[];
  /** The row's own annual total, for the accessible summary. */
  readonly demandHours: number;
}

/** One Platform-and-period cell of the heatmap. */
export interface PlatformHeatmapCell {
  readonly periodId: string;
  readonly periodLabel: string;
  readonly demandHours: number;
  /** The cell's share of the busiest cell in the whole matrix, 0-1, for the fill. */
  readonly intensity: number;
}

/** The annual workload heatmap: Platform rows against period columns (UI spec §5.2). */
export interface PlatformHeatmap {
  readonly periodIds: readonly string[];
  readonly periodLabels: readonly string[];
  readonly rows: readonly PlatformHeatmapRow[];
}

const UNASSIGNED_KEY = '__unassigned__';

/**
 * Groups the driver list along `groupBy`, largest group first (UI spec §5.2).
 *
 * `totalDemandHours` is the overall total the shares are taken against - passed
 * in rather than re-summed so the shares match the figure the endpoint reports,
 * even when a filter narrows the visible drivers.
 */
export function deriveExplorerRows(
  drivers: readonly DemandDriver[],
  groupBy: GroupBy,
  totalDemandHours: number,
  lookups: DemandExplorerLookups,
): readonly DemandExplorerRow[] {
  if (drivers.length === 0) {
    return [];
  }

  const groups = new Map<string, DemandDriver[]>();
  for (const driver of drivers) {
    const key = groupKeyOf(driver, groupBy);
    const bucket = groups.get(key);
    if (bucket === undefined) {
      groups.set(key, [driver]);
    } else {
      bucket.push(driver);
    }
  }

  const rows: DemandExplorerRow[] = [];
  for (const [key, groupDrivers] of groups) {
    const demandHours = sumDemandHours(groupDrivers);
    rows.push({
      key,
      groupLabel: groupLabelOf(groupDrivers, groupBy, key, lookups),
      // `groupKeyOf` returns the driver's `initiativeId` for this grouping and
      // something else for every other one, so the key *is* the id here and is not
      // one anywhere else.
      initiativeId: groupBy === 'initiative' ? key : null,
      initiativeCount: countDistinct(groupDrivers, (driver) => driver.initiativeId),
      driverCount: groupDrivers.length,
      demandHours,
      dataIncompleteCount: groupDrivers.filter((driver) => driver.demandHours === null).length,
      sharePercent: totalDemandHours > 0 ? (demandHours / totalDemandHours) * 100 : 0,
      periodBreakdown: derivePeriodBreakdown(groupDrivers),
    });
  }

  return rows.sort((a, b) => b.demandHours - a.demandHours);
}

/** The table's footing figures (R5.7). See {@link DemandExplorerTotals}. */
export function deriveExplorerTotals(
  drivers: readonly DemandDriver[],
  totalDemandHours: number,
): DemandExplorerTotals {
  return {
    initiativeCount: countDistinct(drivers, (driver) => driver.initiativeId),
    driverCount: drivers.length,
    demandHours: totalDemandHours,
    dataIncompleteCount: drivers.filter((driver) => driver.demandHours === null).length,
  };
}

/**
 * The annual workload heatmap: Demand_Hours per Platform and period (R5.3, R5.4).
 *
 * ## Why this is a product of two allocations
 *
 * The wire carries each driver's two *independent* one-dimensional splits — its
 * hours across Platforms (R5.3) and its hours across periods (R5.4) — and not the
 * two-dimensional matrix, which nothing stores. Both are proportional splits of the
 * same driver total, so the hours a driver puts into (Platform p, period k) are
 *
 * ```
 * platformHours[p] × (periodHours[k] / driverTotal)
 * ```
 *
 * which is exact rather than an estimate: the period split is even distribution
 * across the driver's own window (Assumption A7) and applies uniformly to every
 * Platform the driver serves, so the Platform share of any one period is the
 * Platform share of the whole. Summing that over drivers gives the matrix, and
 * summing the matrix back over either axis returns the corresponding marginal —
 * which is what keeps the heatmap reconcilable with the table beside it.
 *
 * A Data_Incomplete driver contributes nothing (R5.9): its total is `null`, so there
 * is no share to take and no row to draw it on.
 */
export function derivePlatformHeatmap(
  drivers: readonly DemandDriver[],
  platformNames: NameLookup,
  platformColours: ReadonlyMap<string, string | null>,
): PlatformHeatmap {
  const periodIds = new Set<string>();
  const byPlatform = new Map<string, Map<string, number>>();

  for (const driver of drivers) {
    const total = driver.demandHours;
    if (total === null || total <= 0) {
      continue;
    }

    for (const platform of driver.platformAllocations) {
      const cells = byPlatform.get(platform.platformId) ?? new Map<string, number>();
      byPlatform.set(platform.platformId, cells);

      for (const period of driver.periodAllocations) {
        periodIds.add(period.periodId);
        const hours = platform.hours * (period.hours / total);
        cells.set(period.periodId, (cells.get(period.periodId) ?? 0) + hours);
      }
    }
  }

  const orderedPeriods = [...periodIds].sort((a, b) => a.localeCompare(b));

  // The fill scale is the busiest cell in the whole matrix, not per row: a row
  // scaled to its own maximum makes a Platform with 40 hours look as loaded as one
  // with 4,000, which is the opposite of what a heatmap is for.
  let peak = 0;
  for (const cells of byPlatform.values()) {
    for (const hours of cells.values()) {
      peak = Math.max(peak, hours);
    }
  }

  const rows: PlatformHeatmapRow[] = [];
  for (const [platformId, cells] of byPlatform) {
    let demandHours = 0;
    for (const hours of cells.values()) {
      demandHours += hours;
    }

    rows.push({
      platformId,
      label: platformNames.get(platformId) ?? platformId,
      colour: platformColours.get(platformId) ?? null,
      demandHours,
      cells: orderedPeriods.map((periodId) => {
        const hours = cells.get(periodId) ?? 0;
        return {
          periodId,
          periodLabel: formatPeriodLabel(periodId),
          demandHours: hours,
          intensity: peak > 0 ? hours / peak : 0,
        };
      }),
    });
  }

  return {
    periodIds: orderedPeriods,
    periodLabels: orderedPeriods.map(formatPeriodLabel),
    rows: rows.sort((a, b) => b.demandHours - a.demandHours),
  };
}

/** The grouping key for one driver along one dimension. */
function groupKeyOf(driver: DemandDriver, groupBy: GroupBy): string {
  switch (groupBy) {
    case 'initiative':
      return driver.initiativeId;
    case 'ff-role':
      return driver.ffRole;
    case 'activity-type':
      return driver.activityTypeId;
    case 'region':
      return driver.regionId ?? UNASSIGNED_KEY;
  }
}

/**
 * The display label for a group.
 *
 * An id with no master data match renders as the id rather than as blank: a
 * deactivated or newly removed record should still be identifiable in the table
 * (R16.5 keeps existing associations when a record is deactivated).
 */
function groupLabelOf(
  drivers: readonly DemandDriver[],
  groupBy: GroupBy,
  key: string,
  lookups: DemandExplorerLookups,
): string {
  const first = drivers[0];
  switch (groupBy) {
    case 'initiative':
      return first === undefined ? key : `${first.initiativeRef} – ${first.title}`;
    case 'ff-role':
      return ffRoleLabel(key as FieldForceRole) ?? key.toUpperCase();
    case 'activity-type':
      return lookups.activityTypes.get(key) ?? key;
    case 'region':
      return key === UNASSIGNED_KEY ? '(Unassigned)' : (lookups.regions.get(key) ?? key);
  }
}

/** Sums the Demand_Hours of the calculated drivers, skipping Data_Incomplete (R5.9). */
function sumDemandHours(drivers: readonly DemandDriver[]): number {
  let total = 0;
  for (const driver of drivers) {
    if (driver.demandHours !== null) {
      total += driver.demandHours;
    }
  }
  return total;
}

function countDistinct<T>(items: readonly T[], keyOf: (item: T) => string): number {
  const seen = new Set<string>();
  for (const item of items) {
    seen.add(keyOf(item));
  }
  return seen.size;
}

/**
 * Rolls the drivers' period allocations up into one breakdown (UI spec §5.2).
 *
 * Ordered by period id, which sorts chronologically for both month and
 * calendar-week identifiers.
 */
function derivePeriodBreakdown(drivers: readonly DemandDriver[]): readonly PeriodBreakdown[] {
  const byPeriod = new Map<string, number>();
  for (const driver of drivers) {
    for (const allocation of driver.periodAllocations) {
      byPeriod.set(allocation.periodId, (byPeriod.get(allocation.periodId) ?? 0) + allocation.hours);
    }
  }

  let total = 0;
  for (const hours of byPeriod.values()) {
    total += hours;
  }

  const breakdown: PeriodBreakdown[] = [];
  for (const [periodId, hours] of byPeriod) {
    breakdown.push({
      periodId,
      periodLabel: formatPeriodLabel(periodId),
      demandHours: hours,
      sharePercent: total > 0 ? (hours / total) * 100 : 0,
    });
  }

  return breakdown.sort((a, b) => a.periodId.localeCompare(b.periodId));
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** `2027-01` → `Jan`, `2027-W01` → `W01`, anything else unchanged. */
function formatPeriodLabel(periodId: string): string {
  const monthMatch = /^\d{4}-(\d{2})$/.exec(periodId);
  if (monthMatch) {
    const name = MONTH_NAMES[Number.parseInt(monthMatch[1] ?? '1', 10) - 1];
    return name ?? periodId;
  }
  if (/^\d{4}-W\d{2}$/.test(periodId)) {
    return periodId.slice(5);
  }
  return periodId;
}

